import React from 'react';
import { LucideIcon, ChevronDown } from 'lucide-react';
import { NavItem } from './NavItem';
import { useLocation } from 'react-router-dom';

interface NavSectionProps {
    section: string;
    icon: LucideIcon;
    items: any[];
    sidebarOpen: boolean;
    isExpanded: boolean;
    toggleNavSection: (section: string) => void;
}

export const NavSection: React.FC<NavSectionProps> = ({
    section,
    icon: SectionIcon,
    items,
    sidebarOpen,
    isExpanded,
    toggleNavSection
}) => {
    const location = useLocation();
    const hasActiveChild = items.some(item => location.pathname === item.path);

    return (
        <li className="relative">
            {sidebarOpen ? (
                <div
                    onClick={() => toggleNavSection(section)}
                    className={`
                        group relative flex items-center justify-between gap-3 px-3 py-2.5 my-0.5 rounded-xl
                        transition-all duration-200 cursor-pointer
                        ${isExpanded ? 'bg-white/50 dark:bg-gray-700/30' : ''}
                        hover:bg-white/80 dark:hover:bg-gray-700/50 hover:shadow-md
                    `}
                >
                    <div className="flex items-center gap-3 flex-1">
                        <div className={`
                            p-1.5 rounded-lg transition-all duration-200
                            ${hasActiveChild ? 'bg-blue-100 dark:bg-blue-900/40' : 'group-hover:bg-blue-50 dark:group-hover:bg-blue-900/30'}
                        `}>
                            <SectionIcon size={18} className={`transition-colors duration-200 ${hasActiveChild ? 'text-blue-600 dark:text-blue-400' : 'text-slate-600 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400'}`} />
                        </div>
                        <span className={`font-medium text-sm transition-colors duration-200 ${hasActiveChild ? 'text-blue-700 dark:text-blue-300 font-semibold' : 'text-slate-700 dark:text-gray-200 group-hover:text-slate-900 dark:group-hover:text-white'}`}>
                            {section}
                        </span>
                    </div>
                    <div className={`transition-all duration-300 p-1 rounded-lg ${isExpanded ? 'rotate-0 bg-blue-100 dark:bg-blue-900/40' : '-rotate-90 group-hover:bg-slate-100 dark:group-hover:bg-gray-600'}`}>
                        <ChevronDown size={14} className={hasActiveChild ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-gray-400'} />
                    </div>
                </div>
            ) : (
                <div className="pt-3 pb-1">
                    <div className="w-8 h-px bg-[var(--border-color)] mx-auto" />
                </div>
            )}

            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${(isExpanded || !sidebarOpen) ? 'max-h-[32rem] opacity-100 mt-1' : 'max-h-0 opacity-0'}`}>
                <ul className={`${sidebarOpen ? 'ml-4 pl-4 border-l-2 border-blue-200 dark:border-blue-800/50' : ''} space-y-0.5`}>
                    {items.map((item) => (
                        <NavItem
                            key={item.path}
                            {...item}
                            sidebarOpen={sidebarOpen}
                            isActive={item.external ? false : location.pathname === item.path}
                        />
                    ))}
                </ul>
            </div>
        </li>
    );
};
